import {
  Box,
  Typography,
  styled,
  useTheme,
  Skeleton,
  ListSubheader,
  Tooltip,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
} from '@mui/material';
import Dropdown from '../../common/DropDown';
import {
  UserInfoList,
  UserInfoLogoutList,
  UserInfoLogoutItem,
} from '../../../utils/data';
import { useUserStatus } from '../../../Hooks/useUserStatus';
import { useEffect, useState, useContext } from 'react';
import Gravatar from 'react-gravatar';
import { useConfig } from '../../../Hooks/useConfig';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { getCookie, removeCookie, setCookie } from '../../../utils/cookies';
import { useAuthStatus } from '../../../store/auth/useAuthStatus';
import Marquee from 'react-fast-marquee';
import ColorModeContext from '../../../context/ColorModeContext';
import { Moon, Sun1, ColorSwatch } from 'iconsax-react';
import useUsersFacade from '../../../store/profile/useUsersFacade';

const MarqueeContainer = styled(Box)({
  overflow: 'hidden',
  width: '100%',
  whiteSpace: 'nowrap',
  position: 'relative',
  margin: 0,
  padding: 0,
  lineHeight: 1,
  WebkitMaskImage:
    'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
  maskImage:
    'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
});

const UserInfoContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  transition: 'background-color 0.2s linear',
  borderRadius: theme.spacing(2),
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
  '.icon-Arrow-Down': {
    fontSize: '16px',
  },
}));

const UserImage = styled('img')(({ theme }) => ({
  width: theme.spacing(8),
  height: theme.spacing(8),
  border: `1px solid ${theme.palette.secondary.neutral_90}`,
  borderRadius: '100%',

  [theme.breakpoints.down('md')]: {
    width: theme.spacing(8),
    height: theme.spacing(8),
  },
}));
const UserImageGravatara = styled(Gravatar)(({ theme }) => ({
  width: theme.spacing(9),
  height: theme.spacing(9),
  borderRadius: '100%',

  [theme.breakpoints.down('md')]: {
    width: theme.spacing(9),
    height: theme.spacing(9),
  },
}));

const IconListImg = styled('img')(({ theme }) => ({
  width: theme.spacing(6),
  height: theme.spacing(6),
  objectFit: 'contain',
  [theme.breakpoints.down('md')]: {
    width: theme.spacing(5),
    height: theme.spacing(5),
  },
}));

const ListText = styled(ListItemText)(({ theme }) => ({
  '.MuiListItemText-primary': {
    fontSize: '14px !important',
    fontWeight: 600,
    paddingBottom: theme.spacing(1.6),
    [theme.breakpoints.down('md')]: {
      fontSize: '12px !important',
    },
  },
}));

// Custom hook to get user info menu configuration
export const useUserInfoMenu = (userInfoData) => {
  const theme = useTheme();
  const mdDown = useMediaQuery(theme.breakpoints.down('md'));
  const colorMode = useContext(ColorModeContext);
  const { loginState, isLogin } = useUserStatus();
  const [isLoginUser, setIsLoadingUser] = useState(false);
  const [marqueeKey, setMarqueeKey] = useState(0);
  const [isMarqueeFinished, setIsMarqueeFinished] = useState(false);
  const { panelUrl, baseDomain } = useConfig();
  const history = useHistory();
  const { setToken } = useAuthStatus();
  const { resetUserInfo } = useUsersFacade();
  const { t } = useTranslation();

  const Logout = async () => {
    removeCookie('myServices', baseDomain);
    removeCookie('token', baseDomain);
    removeCookie('showFeedbackBanner', baseDomain);
    removeCookie('credit_warning_login_shown', baseDomain);
    removeCookie('credit_warning_playground_shown', baseDomain);

    let theme = getCookie('darkMode');
    localStorage.clear();
    setCookie('darkMode', theme, 34560000, baseDomain);
    setToken('');
    resetUserInfo();
    await history.push('/login');
  };

  const handleMarqueeFinish = () => {
    setIsMarqueeFinished(true);
  };

  const handleMarqueeHover = () => {
    if (isMarqueeFinished) {
      setIsMarqueeFinished(false);
      setMarqueeKey((prev) => prev + 1);
    }
  };

  useEffect(() => {
    if (isLogin()) {
      setIsLoadingUser(true);
    }
  }, [userInfoData]);

  const project = panelUrl.includes(window.location.hostname)
    ? 'panel'
    : 'playground';

  const hasHeader = (
    <>
      {isLoginUser ? (
        <ListSubheader
          sx={{
            backgroundColor: 'transparent',
            px: 4,
            pt: 0,
            pb: 2,
            mb: 2,
            borderBottom: '1px solid',
            borderBottomColor:
              theme.palette.mode === 'dark'
                ? 'secondary.neutral_30'
                : '#E5E5E5',
          }}
        >
          <Typography
            sx={{
              fontSize: {
                xs: '10px !important',
                md: '8px !important',
                lg: '9px !important',
                xl: '10px !important',
              },
              fontWeight: 400,
              lineHeight: '16px',
              color: theme.palette.mode === 'dark' ? '#E1F2FD' : '#515151',
              fontFamily: "'Poppins', sans-serif",
              mb: 0,
            }}
          >
            Signed in as
          </Typography>
          {userInfoData?.email ? (
            userInfoData.email.length > 25 && !mdDown ? (
              <MarqueeContainer onMouseEnter={handleMarqueeHover}>
                <Marquee
                  key={marqueeKey}
                  speed={20}
                  loop={1}
                  gradient={false}
                  pauseOnHover={true}
                  onFinish={handleMarqueeFinish}
                >
                  <Typography
                    sx={{
                      fontSize: {
                        xs: '16px !important',
                        md: '13px !important',
                        lg: '14px !important',
                        xl: '16px !important',
                      },
                      fontWeight: 400,
                      lineHeight: 'normal',
                      color:
                        theme.palette.mode === 'dark' ? '#E1F2FD' : '#515151',
                      fontFamily: "'Poppins', sans-serif",
                      paddingLeft: '10px',
                      paddingRight: '10px',
                    }}
                  >
                    {userInfoData.email}
                  </Typography>
                </Marquee>
              </MarqueeContainer>
            ) : (
              <Typography
                sx={{
                  fontSize: {
                    xs: '16px !important',
                    md: '13px !important',
                    lg: '14px !important',
                    xl: '16px !important',
                  },
                  fontWeight: 400,
                  lineHeight: 'normal',
                  color: theme.palette.mode === 'dark' ? '#E1F2FD' : '#515151',
                  fontFamily: "'Poppins', sans-serif",
                }}
              >
                {userInfoData.email}
              </Typography>
            )
          ) : (
            <Typography>
              <Skeleton />
            </Typography>
          )}
        </ListSubheader>
      ) : (
        false
      )}
    </>
  );

  const hasFooter = (
    <>
      {isLoginUser ? (
        <Box
          sx={{
            backgroundColor: 'transparent',
            borderTop: '1px solid',
            borderColor:
              theme.palette.mode === 'dark'
                ? 'secondary.neutral_30'
                : '#E5E5E5',
          }}
        >
          <ListItem disablePadding>
            <ListItemButton
              sx={{
                py: { xs: 2, md: 3 },
                px: { xs: 3, md: 4 },
                alignItems: 'center',
                '&:hover': {
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? theme.palette.secondary.surface_22
                      : theme.palette.primary.primary_99,
                },
              }}
              onClick={Logout}
            >
              <Box sx={{ mr: { xs: 1.5, md: 2 } }}>
                {UserInfoLogoutItem.type &&
                UserInfoLogoutItem.type === 'image' ? (
                  <IconListImg
                    src={`/static/img/icon/${UserInfoLogoutItem.icon}`}
                  />
                ) : (
                  <UserInfoLogoutItem.icon
                    size={16}
                    style={{ marginBottom: '0px' }}
                  />
                )}
              </Box>
              <ListText
                primary={t(UserInfoLogoutItem.text)}
                sx={{
                  '.MuiListItemText-primary': {
                    color: '#919191',
                    fontSize: {
                      xs: '11px !important',
                      md: '11px !important',
                      lg: '12px !important',
                      xl: '14px !important',
                    },
                    fontWeight: 700,
                    lineHeight: '20px',
                    fontFamily: "'Poppins', sans-serif",
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        </Box>
      ) : (
        false
      )}
    </>
  );

  // Create theme menu item with submenu
  const themeMenuItem = {
    icon: ColorSwatch,
    text: 'Theme',
    type: 'icon',
    submenu: [
      {
        icon: Sun1,
        text: 'Light',
        type: 'icon',
        onClick: () => {
          if (theme.palette.mode === 'dark') {
            colorMode.toggleDarkMode();
          }
        },
      },
      {
        icon: Moon,
        text: 'Dark',
        type: 'icon',
        onClick: () => {
          if (theme.palette.mode === 'light') {
            colorMode.toggleDarkMode();
          }
        },
      },
    ],
  };

  return {
    hasHeader,
    hasFooter,
    list: isLoginUser
      ? [...UserInfoList, themeMenuItem]
      : [...UserInfoLogoutList, themeMenuItem],
    isLoginUser,
  };
};

// Avatar component for user profile
export const UserAvatar = ({ userInfoData, sx }) => {
  const { isLogin } = useUserStatus();
  const [isLoginUser, setIsLoadingUser] = useState(false);

  useEffect(() => {
    if (isLogin()) {
      setIsLoadingUser(true);
    }
  }, [userInfoData]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {isLoginUser ? (
        userInfoData ? (
          <UserImageGravatara email={userInfoData.email} sx={sx} />
        ) : (
          <UserImage src={'/static/img/Profile-Avatar.svg'} sx={sx} />
        )
      ) : (
        <UserImage src={'/static/img/Profile-Avatar.svg'} sx={sx} />
      )}
    </Box>
  );
};

// Keep original UserInfo component for backward compatibility
const UserInfo = ({ userInfoData }) => {
  const { t } = useTranslation();
  const menuConfig = useUserInfoMenu(userInfoData);

  return (
    <Dropdown
      hasHeader={menuConfig.hasHeader}
      hasFooter={menuConfig.hasFooter}
      rightItem
      list={menuConfig.list}
    >
      <Tooltip title={t('global.profile')} placement="bottom">
        <UserInfoContent>
          <UserAvatar userInfoData={userInfoData} />
        </UserInfoContent>
      </Tooltip>
    </Dropdown>
  );
};

export default UserInfo;
